import { Box, Stack, Typography, useTheme } from "@mui/material";
import { ReactComponent as DebtIcon } from "Components/_Shared/Icons/Assets/debt.svg";
import { ReactComponent as FinancialSuccessIcon } from "Components/_Shared/Icons/Assets/financial-success.svg";
import { ReactComponent as HealthIcon } from "Components/_Shared/Icons/Assets/health.svg";
import { ReactComponent as HomeIcon } from "Components/_Shared/Icons/Assets/home.svg";
import { ReactComponent as MoneyBoxIcon } from "Components/_Shared/Icons/Assets/money-box.svg";
import { ReactComponent as ProtectIcon } from "Components/_Shared/Icons/Assets/protect.svg";
import { ReactComponent as StackIcon } from "Components/_Shared/Icons/Assets/stack.svg";
import { ReactComponent as TargetIcon } from "Components/_Shared/Icons/Assets/target.svg";
import { AmountFrequency, type ExpenseLine, ExpenseLineExpenseType } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { type FC, useState } from "react";
import { EXPENSE_TYPE_TRANSLATION_KEY } from "../expenseTypes";
import { EditExpenseDrawer } from "./EditExpenseDrawer";

type Props = {
  expense: ExpenseLine;
};

const getIcon = (expenseType: ExpenseLineExpenseType) => {
  switch (expenseType) {
    case ExpenseLineExpenseType.housing:
      return <HomeIcon />;
    case ExpenseLineExpenseType.consumption:
      return <FinancialSuccessIcon />;
    case ExpenseLineExpenseType.life_style:
      return <HealthIcon />;
    case ExpenseLineExpenseType.loan:
      return <DebtIcon />;
    case ExpenseLineExpenseType["financial-product"]:
      return <MoneyBoxIcon />;
    case ExpenseLineExpenseType.insurance:
      return <ProtectIcon />;
    case ExpenseLineExpenseType.other:
      return <TargetIcon />;
  }
};

export const ExpenseTile: FC<Props> = ({ expense }) => {
  const { t } = useResource();
  const theme = useTheme();

  const [editDrawerOpen, setEditDrawerOpen] = useState(false);

  return (
    <>
      <Stack
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        gap={Spacing.L}
        sx={{
          border: `1px solid ${theme.colors.borderLight}`,
          borderRadius: theme.spacing(Radius.M),
          padding: theme.spacing(Spacing.L),
          boxShadow: theme.shadows[1],
          background: theme.colors.white,
          cursor: "pointer",
        }}
        onClick={() => setEditDrawerOpen(true)}
      >
        {getIcon(expense.expenseType)}

        <Stack spacing={Spacing.M} width="100%" direction="column" justifyContent="flex-start" mt={theme.spacing(Spacing.XS)}>
          <Typography variant="body2" color={theme.colors.textPrimary} fontWeight={600}>
            {t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes[EXPENSE_TYPE_TRANSLATION_KEY[expense.expenseType]])}
          </Typography>

          <Stack direction="row" justifyContent="flex-start" alignItems="center" gap={Spacing.M}>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              gap={Spacing.S}
              sx={{
                height: "22px",
                background: theme.colors.gray,
                padding: `${theme.spacing(Spacing.XS)} ${theme.spacing(Spacing.M)}`,
                borderRadius: theme.spacing(Radius.XL),
              }}
            >
              <StackIcon />

              <Typography variant="caption" color={theme.colors.textPrimary} fontWeight={500}>
                {formatCurrency(expense.amount.value, expense.amount.currencyCode)}
              </Typography>
            </Box>

            <Box
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              gap={Spacing.S}
              sx={{
                height: "22px",
                background: theme.colors.gray,
                padding: `${theme.spacing(Spacing.XS)} ${theme.spacing(Spacing.M)}`,
                borderRadius: theme.spacing(Radius.XL),
              }}
            >
              <Typography variant="caption" color={theme.colors.textPrimary} fontWeight={500}>
                {expense.amount.frequency === AmountFrequency.monthly && t(Resources.Common.Monthly)}
                {expense.amount.frequency === AmountFrequency.yearly && t(Resources.Common.Year)}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Stack>

      <EditExpenseDrawer open={editDrawerOpen} anchor="right" onClose={() => setEditDrawerOpen(false)} expense={expense} />
    </>
  );
};
